/*
 * WaWiKundenApp.tsx - Warenwirtschaftliche Kundendaten.
 *
 * Siehe auch StammKundenApp.
 *
 * Created by Dr. Maximillian Dornseif 2021-10-18 in huWaWi3 21.7.1
 * Copyright (c) 2021, 2023 HUDORA GmbH
 */

import Stack from '@mui/material/Stack'
import React from 'react'
import { navigate } from 'wouter/use-location'

import { GruppenCard } from '../lib/components/GruppenCard'
import { IEntitySubPage3 } from '../lib/components/ke/EntityPage3'
import { KindListe3 } from '../lib/components/ke/KindListe3'
import { KindPage3 } from '../lib/components/ke/KindPage3'
import { KundenCrmCard } from '../lib/components/KundenCrmCard'
import { KUNDEN_LIST_QUERY, KUNDE_ENTITY_QUERY, SUGGESTIONS_QUERY } from '../Stammdaten/StammKundenAppQueries'
import {
  AUFTRAGPOS_ERLEDIGT_LIST_QUERY,
  AUFTRAGPOS_LIST_QUERY,
  BELEG_LIST_QUERY,
  KUNDE_AUFTRAG_ERLEDIGT_LIST_QUERY,
  KUNDE_AUFTRAG_OFFEN_LIST_QUERY,
  RESERVIERUNGSPOSITIONEN_LIST_QUERY,
  RESERVIERUNG_LIST_QUERY,
} from './WawiKundenAppQueries'
import { WaWiKundenKreditanspannung } from './WaWiKundenKreditanspannung'

/** Rendert entweder die Startseite (Suche) oder eine Kundenseite
 * */
export const WaWiKundenApp = (props) => {
  const subPages: IEntitySubPage3[] = [
    {
      title: 'Offene Aufträge',
      path: 'a',
      render: (entity) => (
        <KindListe3
          title={'offene Aufträge'}
          schemaName={'HwAuftrag'}
          listQuery={KUNDE_AUFTRAG_OFFEN_LIST_QUERY}
          listQueryVars={{ nr: entity.designator }}
          hideExcept={[
            'auftragsnr',
            'status',
            'auftragsnr_kunde',
            'anlieferdatum_bis',
            'anlieferdatum_von',
            'created_via',
            'erfasst_von',
            'created_at',
            'designator',
          ]}
          // rowToolsEnabled={true}
        />
      ),
    },
    {
      title: '- Positionen',
      path: 'p',
      render: (entity) => (
        <KindListe3
          title={'offene Auftragspositionen'}
          listQuery={AUFTRAGPOS_LIST_QUERY}
          listQueryVars={{ nr: entity.designator }}
          schemaName="HwAuftragsPosition"
        />
      ),
    },
    {
      title: 'Blockaufträge',
      path: 'r',
      render: (entity) => (
        <KindListe3
          title={'Blockaufträge'}
          schemaName={'HwReservierung'}
          listQuery={RESERVIERUNG_LIST_QUERY}
          listQueryVars={{ nr: entity.designator }}
        />
      ),
    },
    {
      title: '- positionen',
      path: 'n',
      render: (entity) => (
        <KindListe3
          title={'Blockauftragspositionen'}
          schemaName={'HwReservierungsPosition'}
          listQuery={RESERVIERUNGSPOSITIONEN_LIST_QUERY}
          listQueryVars={{ nr: entity.designator }}
          hideExcept={[
            'reservierungsnr',
            'bartnr',
            'menge_bestellt',
            'menge_geliefert',
            'menge_offen',
            'empfaengernr',
            'name',
            'anlieferdatum_von',
            'zeitfenster',
            'anlieferdatum_bis',
            'erledigt',
            'created_at',
          ]}
        />
      ),
    },
    {
      title: 'Belege',
      path: 'belege',
      render: (entity) => (
        <KindListe3
          schemaName={'BBeleg'}
          title={'Belege'}
          listQuery={BELEG_LIST_QUERY}
          listQueryVars={{ nr: entity.designator }}
          hideExcept={['designator', 'belegart', 'belegdatum', 'faktura.warenwert', 'faktura.brutto_betrag']}
        />
      ),
    },
    {
      title: 'Kreditanspannung',
      path: 'kredit',
      render: (entity) => <WaWiKundenKreditanspannung kunde={entity} />,
    },
    {
      title: "erledigte Aufträge",
      path: "erledigt_a",
      render: (entity) => (
        <KindListe3
          title={"erledigte Aufträge"}
          schemaName={"HwAuftrag"}
          listQuery={KUNDE_AUFTRAG_ERLEDIGT_LIST_QUERY}
          listQueryVars={{ nr: entity.designator }}
          hideExcept={[
            "auftragsnr",
            "status",
            "auftragsnr_kunde",
            "anlieferdatum_bis",
            "anlieferdatum_von",
            "created_via",
            "erfasst_von",
            "created_at",
            "designator",
          ]}
          // rowToolsEnabled={true}
        />
      ),
    },
    {
      title: "- Positionen",
      path: "erledigt_P",
      render: (entity) => (
        <KindListe3
          title={"erledigte Positionen"}
          listQuery={AUFTRAGPOS_ERLEDIGT_LIST_QUERY}
          listQueryVars={{ kundennr: entity.designator }}
          schemaName="HwAuftragsPosition"
        />
      ),
    },
  ]

  return (
    <KindPage3
      suggestionQuery={SUGGESTIONS_QUERY}
      basePath={'/wawi'}
      componentPath={'k'}
      initialLimit={250}
      schemaName={'HwKunde'}
      title={'Kunden'}
      subPages={subPages}
      hideExcept={[
        'ist_aktiv',
        'designator',
        'name',
        'bezeichner',
        'adresse.strasse',
        'adresse.plz',
        'adresse.ort',
        'adresse.land',
        'adresse.iln',
        'gruppennr',
        'verbandsnr',
      ]}
      entityQuery={KUNDE_ENTITY_QUERY}
      listQuery={KUNDEN_LIST_QUERY}
      entityCommandBarItems={[
        {
          key: 'stamm',
          text: 'in den Stammdaten',
          ariaLabel: 'Kunden-Stamm',
          iconProps: {
            iconName: 'MasterDatabase',
          },
          iconOnly: false,
          onClick: (e) => {
            navigate(window.location.pathname.replace('/wawi/k/', '/stamm/k/'))
          },
        },
      ]}
      renderEntityPageRightSidebar={(e) => (
        <Stack gap="2em">
          <GruppenCard nr={e?.gruppennr} />
          <KundenCrmCard nr={e?.designator} />
        </Stack>
      )}
    />
  )
}
